import type { FeedBlendItem } from '@/types/entry';

export const MIN_MIX_PERCENT = 1;

/** The second feed of a mix being set up on an unsaved slot; the main feed is the slot's feed type. */
export interface FeedMixDraft {
  feed_type: string;
  percent: string;
}

// The percent is the user's to enter: nothing is saved as a mix until they do.
export const NEW_MIX_DRAFT: FeedMixDraft = { feed_type: '', percent: '' };

export const MIX_INCOMPLETE = ['Choose the second feed', 'Enter the percent'];

export function mixProblem(main: string | null, secondType: string, secondPercent: string): string | null {
  const text = secondPercent.trim();
  if (!secondType) return 'Choose the second feed';
  if (secondType === main) return 'Choose a different feed';
  if (!text) return 'Enter the percent';
  if (!/^\d+(\.\d+)?$/.test(text)) return 'Enter a number such as 12 or 12.5';
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return 'At most 2 decimals';
  const second = Number(text);
  return second < MIN_MIX_PERCENT || second > 100 - MIN_MIX_PERCENT
    ? `Percent must be between ${MIN_MIX_PERCENT} and ${100 - MIN_MIX_PERCENT}`
    : null;
}

// Largest first, as the server stores it (its feed_type follows the first one).
export function buildBlend(main: string, secondType: string, second: number): FeedBlendItem[] {
  const parts = [
    { feed_type: main, percent: Math.round((100 - second) * 100) / 100 },
    { feed_type: secondType, percent: second },
  ];
  return parts.sort((a, b) => b.percent - a.percent);
}

/** The blend a draft mix will send, or why it can't be sent yet. No draft means a plain single feed. */
export function draftBlend(
  main: string | null,
  mix: FeedMixDraft | null | undefined
): { blend: FeedBlendItem[] | null; error: string | null } {
  if (!mix) return { blend: null, error: null };
  const error = mixProblem(main, mix.feed_type, mix.percent);
  if (error || !main) return { blend: null, error: error ?? 'Choose the main feed first' };
  return { blend: buildBlend(main, mix.feed_type, Number(mix.percent)), error: null };
}
